import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "spinner-container"
}
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = { class: "select-buttons-container" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = { class: "text-container" }
const _hoisted_7 = { class: "main-text" }
const _hoisted_8 = { class: "subtext" }
const _hoisted_9 = ["disabled"]
const _hoisted_10 = { class: "card" }
const _hoisted_11 = { class: "action-button-container" }
const _hoisted_12 = ["disabled"]
const _hoisted_13 = { key: 1 }

import { onMounted, ref, computed, toRefs } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "axios";
import functions from "@/use/functions";
import Spinner from "@/components/shared/Spinner.vue";
import DrawForm from "@/components/loan/loc/DrawForm.vue";
import StudentForm from "@/components/loan/loc/StudentForm.vue";
import DrawDisbursements from "../loc/DrawDisbursements.vue";
import PermissionsWrapper from "@/components/wrappers/PermissionsWrapper.vue";
import { Permissions, PermissionLevels } from "@/models/permissions";
import {
  Draw,
  ValidDrawStatusIds,
  MinimumDrawAmountResponse,
  LoanDetails,
} from "@/models/loans";
import { useDrawFormFields } from "@/composables/loan/useDrawForm";
import { useDrawData } from "@/composables/loan/useDrawData";
import { useModificationRecord } from "@/composables/loan/useModificationRecord";
import { useDrawTermination } from "@/composables/loan/useDrawTermination";
import { useDrawActions } from "@/composables/loan/useDrawActions";
import { RootStore } from "@/types/root-store";
import store from "@/store";
import { FormChangesResponse } from "@/models/form";


export default /*@__PURE__*/_defineComponent({
  __name: 'DrawsTab',
  props: {
    loanId: {},
    loanDetails: {},
    availableToDrawAmount: {},
    minimumDrawAmount: {},
    programTypeId: {},
    productTypeId: {},
    lenderId: {},
    onDrawsChanged: { type: Function }
  },
  emits: ["studentUpdated"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const emit = __emit;

const props = __props;

const route = useRoute();
const router = useRouter();

const { loanDetails } = toRefs(props);

const drawFormRef = ref<InstanceType<typeof DrawForm> | null>(null);
const studentFormRef = ref<InstanceType<typeof StudentForm> | null>(null);

const isLoading = ref<boolean>(true);
const draws = ref<Draw[]>([]);
const selectedDraw = ref<Draw | null>(null);
const previousDraw = ref<Draw | null>(null);
const mode = ref<"view" | "add" | "edit">("view");
const availableToDrawAmount = ref<number>(props.availableToDrawAmount);
const minimumDrawAmount = ref<MinimumDrawAmountResponse>(
  props.minimumDrawAmount
);

const { isSubmitting, createDrawAsync, updateDrawAsync } = useDrawActions(
  props.loanId,
  refreshDataAsync
);
const { confirmDrawTerminationAsync } = useDrawTermination(
  selectedDraw,
  refreshDataAsync
);
const { isProviderMethod, confirmModificationRecordGenerationAsync } =
  useModificationRecord(selectedDraw);
const {
  certificationMethodOptions,
  drawStatusOptions,
  enrollmentTermOptions,
  schoolLevelOptions,
  getEnrollmentTermsAsync,
} = useDrawData(props.lenderId, props.productTypeId, props.programTypeId);
const { formFields, validationSchema, skipKeys, keysToRename } =
  useDrawFormFields({
    selectedDraw,
    mode,
    availableToDrawAmount,
    drawStatusOptions,
    certificationMethodOptions,
    schoolLevelOptions,
    enrollmentTermOptions,
    minimumDrawAmount,
    draws,
    loanDetails,
  });

const oidcUser = computed(() => (store.state as RootStore)?.oidcStore?.user);

const canEditDraw = computed(() => {
  if(loanDetails.value.isClosedForDraws) {
    return false;
  }

  return (
    mode.value !== "add" &&
    ValidDrawStatusIds.includes(selectedDraw.value?.drawStatusTypeId || 0)
  );
});

const canAddDraw = computed(() => {
  if(loanDetails.value.isClosedForDraws) {
    return true;
  }

  return mode.value == 'add';
})

async function refreshDataAsync() {
  await props.onDrawsChanged();
  await getDrawsAsync();

  functions.closeModal();
}

function discardChanges() {
  drawFormRef.value?.discardChanges();
  changeMode("view");
}

function handleCancelButtonClick() {
  const options = {
    keysToSkip: skipKeys,
    keysToRename: keysToRename,
  };

  const initialValues = mapDrawCompareObject(
    drawFormRef.value?.initialValuesProp
  );
  const updatedValues = mapDrawCompareObject(drawFormRef.value?.formData);

  const modalHtml = functions.checkFormChanges(
    updatedValues,
    initialValues,
    options
  );

  if (modalHtml) {
    functions.openModal({
      title: "Alert",
      description: "The changes you made have not been saved.",
      buttons: [
        {
          title: "Cancel",
          onClick: () => {
            // exits the Edit mode without saving any changes
            discardChanges();
            functions.closeModal();
          },
        },
        {
          title: "Save and Close",
          onClick: async () => {
            const result = await drawFormRef.value?.validate();
            if (!result?.valid) {
              functions.closeModal();
            } else if (drawFormRef.value?.formData) {
              confirmChanges(drawFormRef.value?.formData);
            }
          },
        },
      ],
    });
  } else {
    discardChanges();
  }
}

function mapDrawCompareObject(selectedDraw: any) {
  return {
    schoolCode: selectedDraw?.schoolCode,
    schoolBranch: selectedDraw?.schoolBranch,
    schoolName: selectedDraw?.schoolName,
    gradeLevel: selectedDraw?.schoolLevelTypeName,
    certificationMethod: selectedDraw?.certificationMethodTypeName,
    enrollmentStatus: selectedDraw?.enrollmentStatusName,
    enrollmentTerm: selectedDraw?.enrollmentTermName,
    drawStatus: selectedDraw?.drawStatusTypeName,
    requestedAmount: selectedDraw?.requestedAmountFormatted,
    certifiedAmount: selectedDraw?.certifiedAmountFormatted,
    cluid: selectedDraw.cluid as string,
    sequence: selectedDraw.sequenceNumber as string, // ignore
    anticipatedGraduationDate: selectedDraw?.anticipatedGraduationDateDisplay,
    loanPeriodStartDate: selectedDraw?.loanPeriodStartDateDisplay,
    loanPeriodEndDate: selectedDraw?.loanPeriodEndDateDisplay,
    createdBy: selectedDraw?.createdBy,
    createdAtUtc: selectedDraw.createdAtUtc,
  };
}

async function handleSubmitAsync(formData: Record<string, any>) {
  await confirmChanges(formData);
}

async function saveChangesAsync(
  formData: Record<string, any>,
  formChanges: FormChangesResponse
) {
  if (mode.value === "add") {
    await createDrawAsync(formData);

    if (draws.value.length > 0) {
      selectDraw(draws.value[draws.value.length - 1]); // select the newest draw
    }
  } else if (mode.value === "edit") {
    cleanEditRequest(formData);

    await updateDrawAsync(formData, selectedDraw.value, formChanges);
  }
}

function openConfirmChangesModal(
  modalHtml: string,
  formData: Record<string, any>,
  formChanges: FormChangesResponse
) {
  functions.openModal({
    title: "Confirm",
    description: "Please review your changes before submitting...",
    html: modalHtml,
    buttons: [
      {
        title: "Cancel",
        onClick: () => {
          // Returns back to the form
          functions.closeModal();
        },
      },
      {
        title: "Save Changes",
        onClick: async () => {
          functions.setModalLoading(true);
          await saveChangesAsync(formData, formChanges);
        },
      },
    ],
  });
}

async function confirmChanges(formData: Record<string, any>) {
  const options = {
    keysToSkip: skipKeys,
    keysToRename: keysToRename,
    commentPrefix:
      mode.value === "edit" ? `Draw Seq. ${formData.sequenceNumber} |` : null,
    userDisplayName: `${oidcUser.value.given_name} ${oidcUser.value.family_name}`,
  };

  const initialValues = mapDrawCompareObject(
    drawFormRef.value?.initialValuesProp
  );
  const updatedValues = mapDrawCompareObject(drawFormRef.value?.formData);

  const modalHtml = functions.checkFormChanges(
    updatedValues,
    initialValues,
    options
  );
  const formChanges = functions.summarizeFormChanges(
    updatedValues,
    initialValues,
    options
  );

  if (modalHtml) {
    openConfirmChangesModal(modalHtml, formData, formChanges);
  } else {
    changeMode("view");
  }
}

const changeMode = (newMode: "view" | "add" | "edit") => {
  if (newMode === "add") {
    previousDraw.value = selectedDraw.value;
    selectedDraw.value = new Draw();
  } else if (newMode === "view" && mode.value === "add") {
    if (previousDraw.value) {
      selectedDraw.value = previousDraw.value;
    } else {
      selectedDraw.value = null;
    }
  }

  mode.value = newMode;
};

const selectDraw = async (draw: Draw) => {
  selectedDraw.value = draw;

  mode.value = "view";
  router.replace({
    query: { ...route.query, drawId: draw.id },
    hash: route.hash,
  });

  const enrolmentTerms = await getEnrollmentTermsAsync(
    false,
    0,
    selectedDraw.value.schoolCode,
    selectedDraw.value.schoolBranch
  );

  if (enrolmentTerms) {
    enrollmentTermOptions.value = enrolmentTerms;
  }
};

async function getDrawsAsync() {
  try {
    const response = await axios(
      `/api/loanservice/loans/${props.loanId}/draws`
    );

    draws.value = response.data;

    if (draws.value.length > 0) {
      selectInitialDraw();
    }
  } catch (error) {
    functions.openModal({
      title: "Oops!",
      description:
        "There was an issue getting the draws. Please try again later.",
    });
  }
}

const selectInitialDraw = () => {
  const queryDrawId = route.query.drawId;

  if (queryDrawId) {
    const matchingDraw = draws.value.find(
      (draw) => draw.id === Number(queryDrawId)
    );

    if (matchingDraw) {
      selectDraw(matchingDraw);
    } else {
      selectDraw(draws.value[0]); // default to first if not in list
    }
  } else if (draws.value.length > 0) {
    selectDraw(draws.value[0]); // default to first if not in query
  }
};

function cleanEditRequest(formData: Record<string, any>) {
  // set loan period start/end date(s) to null if there is no change,
  // in order to bypass the date validation in the request object
  if (
    selectedDraw.value?.loanPeriodStartDate !== null &&
    selectedDraw.value?.loanPeriodStartDate === formData["loanPeriodStartDate"]
  ) {
    formData["loanPeriodStartDate"] = null;
  }

  if (
    selectedDraw.value?.loanPeriodEndDate !== null &&
    selectedDraw.value?.loanPeriodEndDate === formData["loanPeriodEndDate"]
  ) {
    formData["loanPeriodEndDate"] = null;
  }

  if (
    selectedDraw.value?.createdAtUtc !== null &&
    selectedDraw.value?.createdAtUtc === formData["createdAtUtc"]
  ) {
    formData["createdAtUtc"] = null;
  }
}

onMounted(async () => {
  await getDrawsAsync();

  isLoading.value = false;
});

__expose({
  studentFormRef,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(Spinner)
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_2, [
          (_unref(loanDetails).isParentLOC)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createVNode(StudentForm, {
                  ref_key: "studentFormRef",
                  ref: studentFormRef,
                  loanDetails: _unref(loanDetails),
                  draw: selectedDraw.value,
                  student: _unref(loanDetails)?.student,
                  onStudentUpdated: _cache[0] || (_cache[0] = ($event: any) => (emit('studentUpdated', $event))),
                  permissionIds: [_unref(Permissions).Draws],
                  permissionLevel: _unref(PermissionLevels).Update
                }, null, 8, ["loanDetails", "draw", "student", "permissionIds", "permissionLevel"])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_4, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(draws.value, (draw) => {
              return (_openBlock(), _createElementBlock("button", {
                key: draw.id,
                class: _normalizeClass(['icon-button', { secondary: draw != selectedDraw.value }]),
                onClick: ($event: any) => (selectDraw(draw))
              }, [
                _cache[5] || (_cache[5] = _createElementVNode("i", { class: "icon icon-money" }, null, -1)),
                _createElementVNode("div", _hoisted_6, [
                  _createElementVNode("span", _hoisted_7, _toDisplayString(draw.requestedAmountFormatted), 1),
                  _createElementVNode("span", _hoisted_8, "Sequence #" + _toDisplayString(draw.sequenceNumber), 1)
                ])
              ], 10, _hoisted_5))
            }), 128)),
            _createVNode(PermissionsWrapper, {
              permissionIds: [_unref(Permissions).Draws],
              permissionLevel: _unref(PermissionLevels).Create
            }, {
              default: _withCtx(() => [
                (mode.value !== 'edit')
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      class: "icon-button dashed",
                      disabled: canAddDraw.value,
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (changeMode('add')))
                    }, [...(_cache[6] || (_cache[6] = [
                      _createElementVNode("span", { class: "icon" }, "+", -1),
                      _createElementVNode("div", { class: "text-container" }, [
                        _createElementVNode("span", { class: "main-text" }, "Add New")
                      ], -1)
                    ]))], 8, _hoisted_9))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }, 8, ["permissionIds", "permissionLevel"])
          ]),
          _createElementVNode("div", _hoisted_10, [
            _createVNode(PermissionsWrapper, {
              permissionIds: [_unref(Permissions).Draws],
              permissionLevel: _unref(PermissionLevels).Update
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_11, [
                  (canEditDraw.value)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        class: "danger",
                        onClick: _cache[2] || (_cache[2] = 
//@ts-ignore
(...args) => (_unref(confirmDrawTerminationAsync) && _unref(confirmDrawTerminationAsync)(...args)))
                      }, " Terminate "))
                    : _createCommentVNode("", true),
                  (_unref(isProviderMethod) && mode.value === 'view')
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 1,
                        class: "secondary",
                        onClick: _cache[3] || (_cache[3] = 
//@ts-ignore
(...args) => (_unref(confirmModificationRecordGenerationAsync) && _unref(confirmModificationRecordGenerationAsync)(...args)))
                      }, " Generate Modification "))
                    : _createCommentVNode("", true),
                  (canEditDraw.value)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 2,
                        disabled: mode.value === 'edit',
                        onClick: _cache[4] || (_cache[4] = ($event: any) => (changeMode('edit')))
                      }, " Edit ", 8, _hoisted_12))
                    : _createCommentVNode("", true),
                  (mode.value !== 'view')
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 3,
                        onClick: handleCancelButtonClick
                      }, " Cancel "))
                    : _createCommentVNode("", true)
                ])
              ]),
              _: 1
            }, 8, ["permissionIds", "permissionLevel"]),
            (_openBlock(), _createBlock(DrawForm, {
              ref_key: "drawFormRef",
              ref: drawFormRef,
              draw: selectedDraw.value,
              key: selectedDraw.value?.id,
              fields: _unref(formFields),
              validationSchema: _unref(validationSchema),
              mode: mode.value,
              productTypeId: __props.productTypeId,
              programTypeId: __props.programTypeId,
              lenderId: __props.lenderId,
              isSubmitting: _unref(isSubmitting),
              initialValues: selectedDraw.value,
              loanId: __props.loanId,
              minimumDrawAmount: minimumDrawAmount.value,
              loanDetails: _unref(loanDetails),
              onSubmit: handleSubmitAsync
            }, null, 8, ["draw", "fields", "validationSchema", "mode", "productTypeId", "programTypeId", "lenderId", "isSubmitting", "initialValues", "loanId", "minimumDrawAmount", "loanDetails"]))
          ]),
          (mode.value === 'view')
            ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                (selectedDraw.value)
                  ? (_openBlock(), _createBlock(DrawDisbursements, {
                      key: 0,
                      loanId: __props.loanId,
                      draw: selectedDraw.value,
                      onDisbursementChangedAsync: refreshDataAsync
                    }, null, 8, ["loanId", "draw"]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true)
        ]))
  ]))
}
}

})